// File: frontend/src/components/Settings/UserSettings.jsx
// User personal settings component

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { 
  UserIcon,
  PaintBrushIcon,
  BellIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { getUserSettings, updateUserSetting } from '../../services/api';
import { useTheme } from '../../contexts/ThemeContext';
import LoadingSpinner from '../UI/LoadingSpinner';

const UserSettings = () => {
  const [editingSettings, setEditingSettings] = useState({});
  const [tempValues, setTempValues] = useState({});
  const [notification, setNotification] = useState(null);
  
  const queryClient = useQueryClient();
  const { setThemePreference } = useTheme();

  // Common user settings with defaults
  const defaultUserSettings = [
    {
      setting_key: 'theme_preference',
      setting_value: 'light',
      setting_type: 'string',
      category: 'appearance',
      description: 'Tema visual preferido (claro ou escuro)'
    },
    {
      setting_key: 'language_preference',
      setting_value: 'pt-BR',
      setting_type: 'string', 
      category: 'localization',
      description: 'Idioma preferido para a interface'
    },
    {
      setting_key: 'timezone',
      setting_value: 'America/Sao_Paulo',
      setting_type: 'string',
      category: 'localization',
      description: 'Fuso horário para exibir datas e horários'
    },
    {
      setting_key: 'email_notifications',
      setting_value: 'true',
      setting_type: 'boolean',
      category: 'notifications',
      description: 'Receber notificações por email'
    },
    {
      setting_key: 'desktop_notifications',
      setting_value: 'true',
      setting_type: 'boolean',
      category: 'notifications',
      description: 'Exibir notificações no navegador'
    },
    {
      setting_key: 'items_per_page',
      setting_value: '25',
      setting_type: 'number',
      category: 'interface',
      description: 'Número de elementos por página nas tabelas'
    },
    {
      setting_key: 'auto_refresh',
      setting_value: 'true',
      setting_type: 'boolean',
      category: 'interface',
      description: 'Atualizar dados automaticamente'
    },
    {
      setting_key: 'default_view',
      setting_value: 'dashboard',
      setting_type: 'string',
      category: 'interface',
      description: 'Página inicial ao fazer login'
    }
  ];

  // Get user settings
  const { data: settingsData, isLoading, error } = useQuery({
    queryKey: ['userSettings'],
    queryFn: getUserSettings
  });

  // Update setting mutation
  const updateMutation = useMutation({
    mutationFn: ({ key, value }) => updateUserSetting(key, value),
    onSuccess: () => {
      queryClient.invalidateQueries(['userSettings']);
      setNotification({
        type: 'success',
        message: 'Preferência atualizada com sucesso'
      });
      setTimeout(() => setNotification(null), 3000);
    },
    onError: (error) => {
      setNotification({
        type: 'error',
        message: error.response?.data?.message || 'Erro ao atualizar preferência'
      });
      setTimeout(() => setNotification(null), 3000);
    }
  });

  // Merge user settings with defaults
  const getEffectiveSettings = () => {
    if (!settingsData?.data?.settings) {
      return defaultUserSettings;
    }

    const userSettings = Object.values(settingsData.data.settings).flat();
    const userSettingsMap = {};
    userSettings.forEach(setting => {
      userSettingsMap[setting.setting_key] = setting;
    });

    return defaultUserSettings.map(defaultSetting => {
      const userSetting = userSettingsMap[defaultSetting.setting_key];
      return userSetting || defaultSetting;
    });
  };

  // Initialize temp values
  useEffect(() => {
    const effectiveSettings = getEffectiveSettings();
    const values = {};
    effectiveSettings.forEach(setting => {
      values[setting.setting_key] = setting.setting_value;
    });
    setTempValues(values);
  }, [settingsData]);

  const handleEdit = (key) => {
    setEditingSettings(prev => ({ ...prev, [key]: true }));
  };

  const handleSave = (key) => {
    updateMutation.mutate({
      key,
      value: tempValues[key]
    });
    setEditingSettings(prev => ({ ...prev, [key]: false }));
    
    // If theme preference changed, apply it immediately
    if (key === 'theme_preference') {
      setThemePreference(tempValues[key]);
    }
  };

  const handleCancel = (key, originalValue) => {
    setTempValues(prev => ({ ...prev, [key]: originalValue }));
    setEditingSettings(prev => ({ ...prev, [key]: false }));
  };

  const handleInputChange = (key, value) => {
    setTempValues(prev => ({ ...prev, [key]: value }));
  };

  const renderSettingField = (setting) => {
    const key = setting.setting_key;
    const isEditing = editingSettings[key];
    const currentValue = tempValues[key] || setting.setting_value;

    const getInputConfig = () => {
      if (setting.setting_type === 'boolean') {
        return {
          type: 'checkbox',
          checked: currentValue === 'true' || currentValue === true,
          onChange: (e) => handleInputChange(key, e.target.checked.toString())
        };
      }

      // Special selects
      if (key === 'theme_preference') {
        return {
          type: 'select',
          value: currentValue,
          onChange: (e) => handleInputChange(key, e.target.value),
          options: [
            { value: 'light', label: 'Claro' },
            { value: 'dark', label: 'Escuro' },
            { value: 'auto', label: 'Automático (sistema)' }
          ]
        };
      }

      if (key === 'language_preference') {
        return {
          type: 'select',
          value: currentValue,
          onChange: (e) => handleInputChange(key, e.target.value),
          options: [
            { value: 'pt-BR', label: 'Português (Brasil)' },
            { value: 'en-US', label: 'Inglês (EUA)' },
            { value: 'es-ES', label: 'Espanhol' }
          ]
        };
      }

      if (key === 'timezone') {
        return {
          type: 'select',
          value: currentValue,
          onChange: (e) => handleInputChange(key, e.target.value),
          options: [
            { value: 'America/Sao_Paulo', label: 'São Paulo (GMT-3)' },
            { value: 'America/New_York', label: 'Nova York (EST)' },
            { value: 'Europe/London', label: 'Londres (GMT)' },
            { value: 'Europe/Madrid', label: 'Madri (CET)' }
          ]
        };
      }

      if (key === 'items_per_page') {
        return {
          type: 'select',
          value: currentValue,
          onChange: (e) => handleInputChange(key, e.target.value),
          options: [
            { value: '10', label: '10 elementos' },
            { value: '25', label: '25 elementos' },
            { value: '50', label: '50 elementos' },
            { value: '100', label: '100 elementos' }
          ]
        };
      }

      if (key === 'default_view') {
        return {
          type: 'select',
          value: currentValue,
          onChange: (e) => handleInputChange(key, e.target.value),
          options: [
            { value: 'dashboard', label: 'Painel Principal' },
            { value: 'leads', label: 'Leads' },
            { value: 'opportunities', label: 'Oportunidades' },
            { value: 'contacts', label: 'Contatos' },
            { value: 'activities', label: 'Atividades' }
          ]
        };
      }

      return {
        type: 'text',
        value: currentValue,
        onChange: (e) => handleInputChange(key, e.target.value)
      };
    };

    const inputConfig = getInputConfig();

    return (
      <div key={key} className="py-4 border-b border-gray-200 dark:border-gray-700 last:border-b-0">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <dt className="text-sm font-medium text-gray-900">
              {key === 'theme_preference' && 'Tema'}
              {key === 'language_preference' && 'Idioma'}
              {key === 'timezone' && 'Fuso horário'}
              {key === 'email_notifications' && 'Notificações por email'}
              {key === 'desktop_notifications' && 'Notificações do navegador'}
              {key === 'items_per_page' && 'Elementos por página'}
              {key === 'auto_refresh' && 'Atualização automática'}
              {key === 'default_view' && 'Página inicial'}
              {!['theme_preference', 'language_preference', 'timezone', 'email_notifications', 'desktop_notifications', 'items_per_page', 'auto_refresh', 'default_view'].includes(key) && key.replace(/_/g, ' ')}
            </dt>
            <dd className="mt-1 text-sm text-gray-500">
              {setting.description}
            </dd>
          </div>
          
          <div className="ml-4 flex items-center space-x-2">
            {isEditing ? (
              <div className="flex items-center space-x-2">
                <div className="w-48">
                  {inputConfig.type === 'checkbox' ? (
                    <input
                      type="checkbox"
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      {...inputConfig}
                    />
                  ) : inputConfig.type === 'select' ? (
                    <select
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      {...inputConfig}
                    >
                      {inputConfig.options.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      {...inputConfig}
                    />
                  )}
                </div>
                <button
                  onClick={() => handleSave(key)}
                  disabled={updateMutation.isPending}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {updateMutation.isPending ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <CheckCircleIcon className="h-4 w-4" />
                  )}
                </button>
                <button
                  onClick={() => handleCancel(key, setting.setting_value)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Cancelar
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <dd className="text-sm text-gray-900">
                  {setting.setting_type === 'boolean' 
                    ? (currentValue === 'true' ? 'Ativo' : 'Inativo')
                    : inputConfig.options 
                      ? inputConfig.options.find(opt => opt.value === currentValue)?.label || currentValue
                      : currentValue
                  }
                </dd>
                <button
                  onClick={() => handleEdit(key)}
                  className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                >
                  Editar
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <LoadingSpinner />
        <p className="text-center text-gray-600 mt-4">Carregando preferências...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Erro ao carregar preferências
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error.message}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const effectiveSettings = getEffectiveSettings();
  
  // Group settings by category
  const groupedSettings = effectiveSettings.reduce((acc, setting) => {
    const category = setting.category || 'general';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(setting);
    return acc;
  }, {});

  const categoryIcons = {
    appearance: PaintBrushIcon,
    localization: ClockIcon,
    notifications: BellIcon,
    interface: UserIcon
  };

  const categoryNames = {
    appearance: 'Aparência',
    localization: 'Localização',
    notifications: 'Notificações',
    interface: 'Interface'
  };

  return (
    <div className="p-6">
      {/* Notification */}
      {notification && (
        <div className={`mb-6 rounded-md p-4 ${
          notification.type === 'success' ? 'bg-green-50' : 'bg-red-50'
        }`}>
          <div className="flex">
            {notification.type === 'success' ? (
              <CheckCircleIcon className="h-5 w-5 text-green-400" />
            ) : (
              <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
            )}
            <div className="ml-3">
              <p className={`text-sm font-medium ${
                notification.type === 'success' ? 'text-green-800' : 'text-red-800'
              }`}>
                {notification.message}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Settings Groups */}
      {Object.entries(groupedSettings).map(([category, categorySettings]) => {
        const Icon = categoryIcons[category] || UserIcon;
        const categoryName = categoryNames[category] || category;
        
        return (
          <div key={category} className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <Icon className="h-5 w-5 mr-2" />
              {categoryName}
            </h3>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <dl className="px-6 py-4">
                {categorySettings.map(renderSettingField)}
              </dl>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default UserSettings;